<template>
	<section class="container py-500-md pt-300-smo pb-200-smo">
		<BasePlaceHolder
			:placehold="isLoading"
			:section-count="4"
			:class="{ 'display-flex flex-direction-column gap-100-md gap-50': !isLoading }"
		>
			<div class="display-flex flex-direction-column gap-50 col-8-lg">
				<h2 class="heading-1">Featured offerings</h2>
				<p class="body-md mt-0"
					>These are accredited-only, closed-end funds that invest according to specific investment strategy
					or take advantage of unique windows of opportunity. They typically have higher investment minimums
					with limited or no&nbsp;liquidity.</p
				>
			</div>
			<OfferingCard
				v-if="ocfOffering"
				:offering="ocfOffering"
				:min-investment="ocfMinInvestment"
				:apply-hover="false"
			>
				<template #additional-data>
					<FundKeyStats v-if="ocfOfferingKeyStats.length" :statistics="ocfOfferingKeyStats" class="mt-150" />
				</template>
			</OfferingCard>
			<OfferingCard :offering="ofundOffering" min-investment="25000" :apply-hover="false" />
			<OfferingCard :offering="stnOffering" min-investment="5000" max-investment="250000" :apply-hover="false" />
		</BasePlaceHolder>
	</section>
</template>

<script setup lang="ts">
import { buildFundKeyStats } from '@utils/offerings';
import { Fund } from 'types/funds';
import { FundKeyStatistic } from 'types/reit-details';
import FundKeyStats from '@components/offering-details/fund-key-stats.vue';
import { getFund } from '@api/sanity/proxy/funds';
import { ocfReitId } from '@constants/reit';
import OfferingCard from '@components/offerings/offering-card.vue';
import { ref } from 'vue';

const ofundOffering: Fund = {
	fundriseId: '',
	name: 'Fundrise Opportunity Fund',
	assetClass: {
		_id: '',
		label: 'Private credit',
		enumValue: 'PRIVATE_CREDIT'
	},
	fundSummary:
		'Fundrise launched one of the first and largest Opportunity Funds in the country in 2018, allowing thousands of Fundrise investors to take advantage of the one-of-a-kind tax benefits associated with Opportunity Zones during the limited window provided by Congress. Opportunity Zones are census tracts designated by state and federal governments targeted for economic development. <br><br> By investing in the Opportunity Fund, investors were able to not only defer and reduce their existing capital gains tax liability, but also eliminate future capital gains taxes on any returns earned from the Opportunity Fund investment itself.',
	fundAumValue: '',
	fundAum: '',
	directInvestmentDetails: {
		offeringVintageStatus: 'SALE_PAUSED',
		currentVintageSaleIsAllowed: false,
		directInvestmentEligibility: 'INELIGIBLE_INVALID_ENTITY',
		reitOfferingVintageId: '',
		accreditedOnly: true,
		entityEligibleForEDirectInvestmentIntoReit: false,
		eligibleForRepeatCheckout: false
	},
	inceptionDate: '2018-02-01',
	objective: {
		_id: '',
		name: 'Growth'
	},
	category: {
		_id: '',
		name: 'PREMIUM'
	},
	offeringAvailabilityStatus: {
		name: 'CLOSED',
		label: 'Closed'
	},
	sanityId: '',
	requiresContingencyPlan: false,
	defaultAllocation: false,
	requiresK1Acknowledgment: false
};

const stnOffering: Fund = {
	fundriseId: '',
	name: 'Short-Term Notes',
	assetClass: {
		_id: '',
		label: 'Private credit',
		enumValue: 'PRIVATE_CREDIT'
	},
	fundSummary: `Fundrise's Short-Term Notes product offered accredited investors extraordinarily competitive interest rates with relatively short hold periods, during a period of time when interest rates available to investors via CDs or money market accounts were often less than 1% (2020-2022). <br><br> This was a true win-win for the Fundrise platform and our investors, as it allowed our real estate portfolio access to a flexible source of temporary borrowing, which in turn allowed us to better manage cash flow, close on the best investment opportunities when they present themselves, and keep our investors' long-term capital efficiently deployed.`,
	fundAumValue: '',
	fundAum: '',
	directInvestmentDetails: {
		offeringVintageStatus: 'SALE_PAUSED',
		currentVintageSaleIsAllowed: false,
		directInvestmentEligibility: 'INELIGIBLE_INVALID_ENTITY',
		reitOfferingVintageId: '',
		accreditedOnly: true,
		entityEligibleForEDirectInvestmentIntoReit: false,
		eligibleForRepeatCheckout: false
	},
	inceptionDate: '2020-11-01',
	objective: {
		_id: '',
		name: 'Cash flow'
	},
	category: {
		_id: '',
		name: 'PREMIUM'
	},
	offeringAvailabilityStatus: {
		name: 'CLOSED',
		label: 'Closed'
	},
	sanityId: '',
	requiresContingencyPlan: false,
	defaultAllocation: false,
	requiresK1Acknowledgment: false
};
const ocfOffering = ref<Fund | null>(null);

const ocfOfferingKeyStats = ref<Array<FundKeyStatistic>>([]);

const ocfMinInvestment = ref('');

const isLoading = ref(true);

async function setup(): Promise<void> {
	try {
		const ocfFundDetails = await getFund(ocfReitId, { include: ['ANNUALIZED_RETURNS'] });

		if (ocfFundDetails) {
			ocfOffering.value = ocfFundDetails;
			ocfOfferingKeyStats.value = buildFundKeyStats(ocfFundDetails);
			ocfMinInvestment.value = ocfFundDetails.minimumInitialInvestmentAmount ?? '';
		}
	} catch {
	} finally {
		isLoading.value = false;
	}
}

setup();
</script>
